import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, firstValueFrom, Observable, Subscription, timeout} from 'rxjs';
import {CommandService} from '@core/services/command.service';
import {SocketClusterService} from '@core/services/socket-cluster.service';
import {PlatformCommand, PlatformCommandResponse} from '@shared/commands/command-base';
import {EventStatus} from '@shared/models/notification-message.model';
import {CallMessageType, CallService, CallStatus, CallType} from '../call.service';

/** One missed call as its banner shows it: the server's MissedCallView. */
export interface MissedCall {
  invitationId: string;
  /** 'Missed' when the call rang out, 'Busy' when it came while this user was in a call or a meeting. */
  status: string;
  callerName: string;
  callerEmail: string;
  /** ISO time the call came. */
  atUtc?: string;
  /** The call's Type, 'Voice' or 'Video' (ITP-E-182-06): "Missed voice call" or "Missed video call". Absent means voice. */
  type?: string;
}

/**
 * The user's missed calls (ITP-E-171-01): the ones the server still holds on page load (GetMissedCallsCommand),
 * and each CallMissed that arrives on the user channel — a ring that ran out, or a call that found this user busy.
 * A missed call stays until it is dismissed (DismissMissedCallCommand) or its caller is called back, which the
 * server also counts as answering it. There is no call log (D-11): a dismissed missed call is gone.
 */
@Injectable({providedIn: 'root'})
export class MissedCallService implements OnDestroy {
  static readonly commandTimeoutMs = 30000;

  private readonly missedSubject = new BehaviorSubject<MissedCall[]>([]);
  /** Newest first. */
  readonly missedCalls$: Observable<MissedCall[]> = this.missedSubject.asObservable();
  private readonly messages: Subscription;
  private loadRequested = false;

  constructor(private commandService: CommandService,
              private socketClusterService: SocketClusterService,
              private callService: CallService) {
    this.messages = this.socketClusterService.onCallMessage().subscribe(message => this.onMessage(message));
  }

  get missedCalls(): MissedCall[] {
    return this.missedSubject.value;
  }

  /** Shows the missed calls the server still holds. Asked once per page load. */
  async load(): Promise<void> {
    if (this.loadRequested) {
      return;
    }
    this.loadRequested = true;
    const response = await this.execute('GetMissedCallsCommand', {});
    if (!response || response.status === EventStatus.Failed || !response.fieldValues) {
      return;
    }
    const loaded = (MissedCallService.parse(response.fieldValues['MissedCalls']) ?? [])
      .map(MissedCallService.fromView)
      .filter(call => !!call.invitationId);
    // A CallMissed may have arrived while the list was on its way: keep it, without a duplicate.
    const live = this.missedCalls.filter(call => !loaded.some(o => o.invitationId === call.invitationId));
    this.missedSubject.next([...live, ...loaded]);
  }

  /** The banner's close button. */
  async dismiss(invitationId: string): Promise<void> {
    if (!this.missedCalls.some(call => call.invitationId === invitationId)) {
      return;
    }
    this.missedSubject.next(this.missedCalls.filter(call => call.invitationId !== invitationId));
    await this.execute('DismissMissedCallCommand', {InvitationId: invitationId});
  }

  /**
   * The banner's Call back: every missed call from that person closes, and the call starts — a missed video call is
   * called back as one (ITP-E-182-06), as Call again does. The server closes them too when StartCallCommand arrives, so
   * a refresh does not bring them back.
   */
  async callBack(missed: MissedCall): Promise<void> {
    const email = missed.callerEmail?.toLowerCase();
    this.missedSubject.next(this.missedCalls.filter(call => call.callerEmail?.toLowerCase() !== email));
    await this.callService.start(missed.callerEmail, missed.callerName, missed.type === CallType.Video);
  }

  ngOnDestroy(): void {
    this.messages.unsubscribe();
  }

  private onMessage(notification: any): void {
    // The publisher may nest the message under Payload, as the websocket layer allows for.
    const message = notification?.invitationId || notification?.InvitationId
      ? notification
      : (notification?.Payload || notification?.payload || notification);
    const messageType = message?.messageType || message?.MessageType || notification?.messageType;
    if (messageType !== CallMessageType.Missed) {
      return;
    }
    const missed: MissedCall = {
      invitationId: message.invitationId || message.InvitationId,
      status: message.status || message.Status || CallStatus.Missed,
      callerName: message.callerName || message.CallerName,
      callerEmail: message.callerEmail || message.CallerEmail,
      atUtc: message.atUtc || message.AtUtc,
      type: message.type || message.Type
    };
    if (!missed.invitationId || this.missedCalls.some(call => call.invitationId === missed.invitationId)) {
      return;
    }
    this.missedSubject.next([missed, ...this.missedCalls]);
  }

  private static fromView(view: any): MissedCall {
    return {
      invitationId: view?.InvitationId ?? view?.invitationId,
      status: view?.Status ?? view?.status,
      callerName: view?.CallerName ?? view?.callerName,
      callerEmail: view?.CallerEmail ?? view?.callerEmail,
      atUtc: view?.AtUtc ?? view?.atUtc,
      type: view?.Type ?? view?.type
    };
  }

  /** The list may arrive as an array or as its JSON. */
  private static parse(value: any): any[] | null {
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    }
    return Array.isArray(value) ? value : null;
  }

  private async execute(name: string, fieldValues: Record<string, string>): Promise<PlatformCommandResponse> {
    const command = new PlatformCommand(name, 'CollaborationApi');
    Object.entries(fieldValues).forEach(([key, value]) => command.fieldValues[key] = value);
    command.waitForResponse = true;
    try {
      // executePlatformCommand does not error on a transport failure, so a bounded wait stands in for one.
      return await firstValueFrom(
        this.commandService.executePlatformCommand(command).pipe(timeout(MissedCallService.commandTimeoutMs)),
        {defaultValue: null});
    } catch {
      return null;
    }
  }
}
